import React from "react";

import { loginUrl } from "@/mk2/apps/users/urls";
import { WikiArticleQuestionEntity } from "@/mk2/apps/wiki/schemas";
import { wikiAddQuestionUrl } from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { url } from "@/mk2/helpers/urls";
import { UserEntity } from "@/mk2/schemas";
import {
    STROLLER_ADDQUESTION_HEADING,
    STROLLER_QUESTIONS_AND_ANSWERS,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

import styles from "./QuestionsAndAnswersSection.mscss";

interface FaqItem {
    question: string;
    answer: string;
}

interface Props {
    seoFaqRichSnippet: string;
    answeredQuestions: WikiArticleQuestionEntity[];
    questionsAllowed: boolean;
    showHeading?: boolean;
    categorySlug: string;
    articleSlug: string;
    requestUser: UserEntity;
}

function parseFaqFromJsonLd(jsonLd: string): FaqItem[] | null {
    try {
        const data = JSON.parse(jsonLd);
        if (data?.["@type"] !== "FAQPage" || !Array.isArray(data?.mainEntity)) {
            return null;
        }
        return data.mainEntity
            .filter((q) => q?.["@type"] === "Question" && q?.name)
            .map((q) => ({
                question: q.name,
                answer: Array.isArray(q.acceptedAnswer)
                    ? q.acceptedAnswer[0]?.text || ""
                    : q.acceptedAnswer?.text || "",
            }))
            .filter((item) => item.question && item.answer);
    } catch {
        return null;
    }
}

const QuestionsAndAnswersSection: React.FC<Props> = ({
    seoFaqRichSnippet,
    answeredQuestions,
    questionsAllowed,
    showHeading,
    categorySlug,
    articleSlug,
    requestUser,
}) => {
    const faqItems = seoFaqRichSnippet ? parseFaqFromJsonLd(seoFaqRichSnippet) : null;

    // Merge both sources: answered user questions first, then FAQ from rich snippet
    const allItems: FaqItem[] = [
        ...answeredQuestions.map((q) => ({ question: q.question, answer: q.answer })),
        ...(faqItems || []),
    ];

    const addQuestionUrl = url(wikiAddQuestionUrl, { categorySlug, articleSlug });

    return (
        <div className={styles.QuestionsAndAnswersSection}>
            {showHeading && (
                <h2 className={styles.QuestionsAndAnswersSection__heading}>
                    {STROLLER_QUESTIONS_AND_ANSWERS}
                </h2>
            )}
            {allItems.length > 0 &&
                allItems.map((item, index) => (
                    <div key={index} className={styles.QuestionsAndAnswersSection__item}>
                        <h3 className={styles.QuestionsAndAnswersSection__question}>
                            {item.question}
                        </h3>
                        <p className={styles.QuestionsAndAnswersSection__answer}>
                            {item.answer.replace(/\\n/g, "\n")}
                        </p>
                    </div>
                ))}

            {questionsAllowed && (
                <div className={styles.QuestionsAndAnswersSection__addQuestion}>
                    <Btn
                        type={BtnType.Blue}
                        label={STROLLER_ADDQUESTION_HEADING}
                        link={
                            isAuthenticated(requestUser)
                                ? addQuestionUrl
                                : url(loginUrl, {}, { next: addQuestionUrl })
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default QuestionsAndAnswersSection;
